/**
 * Delay accessorial builder
 */

import { AccessorialType } from '../../constants/accessorial-types.js'
import type { Accessorial } from '../../types/accessorial.js'
import type { BuildDelayOptions } from './types.js'
import { calcDelayPrice, calcDelayPay } from '../shared/calculations.js'
import { buildAccessorial } from '../shared/buildAccessorial.js'

/**
 * Build Delay Accessorial
 *
 * Cost Responsibility Breakdown:
 * - HopDrive: No direct cost (always 0)
 * - Customer: Pays for driver idle time if responsible
 * - Driver: Paid for their idle time
 *
 * @param options - Delay accessorial options
 * @returns Insertable delay accessorial object
 * @throws Error if validation fails
 *
 * @example
 * ```typescript
 * const delayAccessorial = buildDelayAccessorial({
 *   moveId: 12345,
 *   customerResponsible: true,
 *   driverIdleTimeMinutes: 55,
 *   customerPricePerMinute: 0.33,
 *   driverPayPerMinute: 0.17
 * })
 * // Result: { cost: 0, ar_amount: 18.15, ap_amount: 9.35, ... }
 * ```
 */
export function buildDelayAccessorial(options: BuildDelayOptions): Accessorial {
  // Validation for required props
  if (!options || typeof options !== 'object') {
    throw new Error('Failed to build delay accessorial: Invalid options prop!')
  }
  if (!options.moveId || (typeof options.moveId !== 'number' && typeof options.moveId !== 'bigint')) {
    throw new Error('Failed to build delay accessorial: Invalid moveId prop!')
  }
  if (typeof options.customerResponsible !== 'boolean') {
    throw new Error('Failed to build delay accessorial: Invalid customerResponsible prop!')
  }
  if (typeof options.driverIdleTimeMinutes !== 'number') {
    throw new Error('Failed to build delay accessorial: Invalid driverIdleTimeMinutes prop!')
  }
  if (options.customerPricePerMinute !== undefined && typeof options.customerPricePerMinute !== 'number') {
    throw new Error('Failed to build delay accessorial: Invalid customerPricePerMinute prop!')
  }
  if (options.driverPayPerMinute !== undefined && typeof options.driverPayPerMinute !== 'number') {
    throw new Error('Failed to build delay accessorial: Invalid driverPayPerMinute prop!')
  }
  if (options.notes !== undefined && options.notes !== null && typeof options.notes !== 'string') {
    throw new Error('Failed to build delay accessorial: Invalid notes prop!')
  }

  // Calculate delay price to customer (if customer is responsible)
  const delayPrice = options.customerResponsible
    ? calcDelayPrice(options.driverIdleTimeMinutes, options.customerPricePerMinute)
    : 0

  // Calculate delay pay to driver
  const delayPay = calcDelayPay(options.driverIdleTimeMinutes, options.driverPayPerMinute)

  // Build accessorial using base builder
  return buildAccessorial({
    moveId: options.moveId,
    code: AccessorialType.DELAY.code,
    cost: 0,
    ar_amount: delayPrice,
    ap_amount: delayPay,
    notes: options.notes || AccessorialType.DELAY.desc,
  })
}
